/**
 * GameCharacter — base class for all characters in combat.
 *
 * Both PlayerCharacter and EnemyCharacter extend this.
 * Provides shared combat stats: HP, attack, armor, alive/dead,
 * damage (through armor), healing, and status effects.
 */

import { StatusEffectBag } from "../../framework/character";

export class GameCharacter {
  readonly name: string;
  hp: number;
  maxHp: number;
  /** Backing field for attack. Subclasses can override the getter. */
  _attack: number;
  armor: number;

  /** Active status effects (buffs/debuffs). */
  readonly effects = new StatusEffectBag();

  _dead = false;
  onDeath?: () => void;

  constructor(name: string, maxHp: number, attack: number, armor: number = 0) {
    this.name = name;
    this.maxHp = maxHp;
    this.hp = maxHp;
    this._attack = attack;
    this.armor = armor;
  }

  get attack(): number {
    return this._attack;
  }

  set attack(value: number) {
    this._attack = value;
  }

  get alive(): boolean {
    return !this._dead;
  }

  get dead(): boolean {
    return this._dead;
  }

  /**
   * Deal damage through armor first. Returns actual HP damage dealt.
   * Armor absorbs as much as it can before HP is reduced.
   */
  damage(amount: number): number {
    if (this._dead) return 0;
    const absorbed = Math.min(this.armor, amount);
    this.armor -= absorbed;
    const hpDamage = amount - absorbed;
    if (hpDamage > 0) {
      this.hp = Math.max(0, this.hp - hpDamage);
      if (this.hp === 0 && !this._dead) {
        this._dead = true;
        this.onDeath?.();
      }
    }
    return hpDamage;
  }

  /** Restore HP, clamped at maxHp. */
  heal(amount: number): void {
    this.hp = Math.min(this.maxHp, this.hp + amount);
  }
}
